import React, { useMemo, useState } from 'react';
import {
  Archive,
  ArrowLeft,
  Ban,
  CheckCheck,
  Download,
  MailOpen,
  MoreVertical,
  Pencil,
  Send,
  Smile,
  Trash2,
  User,
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useLineActionCall, useLineRead, useInvalidateLineRead, type Obj } from '@/client/api/whatsapp/line-actions';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  ActionButton,
  asArray,
  ConfirmDialog,
  Empty,
  ErrorNote,
  isGroupJid,
  jidUser,
  Loading,
  phoneLabel,
  toastError,
  useActionAccess,
} from './shared';

const PAGE = 30;

const messageText = (m?: Obj): string => {
  const msg = m?.message ?? m;
  if (!msg) return '';
  return (
    msg.conversation ||
    msg.extendedTextMessage?.text ||
    msg.imageMessage?.caption ||
    msg.videoMessage?.caption ||
    msg.documentMessage?.caption ||
    msg.documentMessage?.fileName ||
    msg.reactionMessage?.text ||
    msg.editedMessage?.message?.protocolMessage?.editedMessage?.conversation ||
    ''
  );
};

const mediaKind = (m?: Obj): string | null => {
  const msg = m?.message;
  if (!msg) return null;
  for (const k of ['imageMessage', 'videoMessage', 'audioMessage', 'documentMessage', 'stickerMessage']) if (msg[k]) return k.replace('Message', '');
  return null;
};

const ts = (v: unknown) => {
  const n = Number(typeof v === 'object' && v ? (v as { low?: number }).low : v);
  return n ? moment.unix(n) : null;
};

const MediaView: React.FC<{ instance: string; msg: Obj }> = ({ instance, msg }) => {
  const call = useLineActionCall();
  const [data, setData] = useState<{ base64: string; mimetype: string; fileName?: string } | null>(null);
  const kind = mediaKind(msg);
  if (!kind) return null;
  const load = () =>
    call.mutate(
      { instance, action: 'chat.getBase64FromMediaMessage', body: { message: { key: msg.key } } },
      { onSuccess: (d) => setData(d), onError: (e) => toastError(e, 'Could not load the media') },
    );
  if (!data) {
    return (
      <ActionButton action="chat.getBase64FromMediaMessage" size="sm" variant="outline" onClick={load} disabled={call.isPending}>
        <Download /> {call.isPending ? 'Loading media...' : `Load ${kind}`}
      </ActionButton>
    );
  }
  const src = `data:${data.mimetype};base64,${data.base64}`;
  if (data.mimetype?.startsWith('image/')) return <img src={src} alt="" className="max-h-64 rounded" />;
  if (data.mimetype?.startsWith('video/')) return <video src={src} controls className="max-h-64 rounded" />;
  if (data.mimetype?.startsWith('audio/')) return <audio src={src} controls />;
  return (
    <a href={src} download={data.fileName || 'file'} className="text-sm text-blue-700 underline">
      Download {data.fileName || 'file'}
    </a>
  );
};

type Dlg =
  | { kind: 'react' | 'edit'; msg: Obj }
  | { kind: 'delete' | 'block' | 'unblock' | 'archive'; msg?: Obj }
  | { kind: 'profile' };

const Thread: React.FC<{ instance: string; jid: string; name: string; onBack: () => void }> = ({ instance, jid, name, onBack }) => {
  const [count, setCount] = useState(PAGE);
  const [dlg, setDlg] = useState<Dlg | null>(null);
  const [text, setText] = useState('');
  const [reply, setReply] = useState('');
  const call = useLineActionCall();
  const invalidate = useInvalidateLineRead();
  const { can } = useActionAccess();

  const q = useLineRead<Obj>(instance, 'chat.findMessages', {
    body: { where: { key: { remoteJid: jid } }, page: 1, offset: count },
    refetchInterval: 15_000,
  });
  const total = q.data?.messages?.total ?? 0;
  const records = useMemo(() => [...asArray<Obj>(q.data?.messages)].sort((a, b) => Number(ts(a.messageTimestamp)?.unix() ?? 0) - Number(ts(b.messageTimestamp)?.unix() ?? 0)), [q.data]);
  const lastKey = records.length ? records[records.length - 1].key : undefined;
  const number = isGroupJid(jid) ? jid : jidUser(jid);

  const run = (action: string, body: Obj, done: string, after?: () => void) =>
    call.mutate(
      { instance, action, body },
      {
        onSuccess: () => {
          toast.success(done);
          setDlg(null);
          after?.();
          invalidate(instance, 'chat.findMessages');
          invalidate(instance, 'chat.findChats');
        },
        onError: (e) => toastError(e, 'The action failed'),
      },
    );

  const keyOf = (m: Obj) => ({ id: m.key.id, fromMe: !!m.key.fromMe, remoteJid: m.key.remoteJid, participant: m.key.participant });

  const confirm = () => {
    if (!dlg) return;
    if (dlg.kind === 'delete' && dlg.msg) run('chat.deleteMessageForEveryone', keyOf(dlg.msg), 'Message deleted for everyone');
    if (dlg.kind === 'block') run('chat.updateBlockStatus', { number, status: 'block' }, 'Contact blocked');
    if (dlg.kind === 'unblock') run('chat.updateBlockStatus', { number, status: 'unblock' }, 'Contact unblocked');
    if (dlg.kind === 'archive') run('chat.archiveChat', { lastMessage: { key: lastKey }, archive: true }, 'Chat archived', onBack);
  };

  const row = (m: Obj) => {
    const mine = !!m.key?.fromMe;
    const when = ts(m.messageTimestamp);
    const body = messageText(m);
    return (
      <div key={m.id ?? m.key?.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${mine ? 'bg-green-100' : 'bg-white border border-gray-200'}`}>
          <div className="mb-0.5 flex items-center justify-between gap-3 text-xs text-gray-500">
            <span>{mine ? 'This line' : m.pushName || phoneLabel(m.key?.participant || m.key?.remoteJid)}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label="Message actions" className="rounded p-1 hover:bg-black/5">
                  <MoreVertical className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={!can('message.sendReaction')} onClick={() => setDlg({ kind: 'react', msg: m })}>
                  <Smile className="mr-2 h-4 w-4" /> React
                </DropdownMenuItem>
                <DropdownMenuItem disabled={!can('chat.markMessageAsRead')} onClick={() => run('chat.markMessageAsRead', { readMessages: [keyOf(m)] }, 'Marked as read')}>
                  <CheckCheck className="mr-2 h-4 w-4" /> Mark as read
                </DropdownMenuItem>
                {mine && messageText(m) && !mediaKind(m) && (
                  <DropdownMenuItem disabled={!can('chat.updateMessage')} onClick={() => { setText(body); setDlg({ kind: 'edit', msg: m }); }}>
                    <Pencil className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                {mine && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-red-600" disabled={!can('chat.deleteMessageForEveryone')} onClick={() => setDlg({ kind: 'delete', msg: m })}>
                      <Trash2 className="mr-2 h-4 w-4" /> Delete for everyone
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {body && <p className="whitespace-pre-wrap break-words text-gray-900">{body}</p>}
          <MediaView instance={instance} msg={m} />
          {!body && !mediaKind(m) && <p className="text-xs italic text-gray-500">{m.messageType || 'Unsupported message'}</p>}
          <div className="mt-0.5 text-right text-[11px] text-gray-500">{when ? when.format('D MMM, HH:mm') : ''}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> All chats
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Chat actions" className="rounded-lg p-2 hover:bg-gray-100">
              <MoreVertical className="h-4 w-4 text-gray-600" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={!can('chat.fetchProfile')} onClick={() => setDlg({ kind: 'profile' })}>
              <User className="mr-2 h-4 w-4" /> View profile
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!can('chat.markChatUnread') || !lastKey} onClick={() => run('chat.markChatUnread', { lastMessage: { key: lastKey }, chat: jid }, 'Marked unread')}>
              <MailOpen className="mr-2 h-4 w-4" /> Mark unread
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!can('chat.archiveChat') || !lastKey} onClick={() => setDlg({ kind: 'archive' })}>
              <Archive className="mr-2 h-4 w-4" /> Archive
            </DropdownMenuItem>
            {!isGroupJid(jid) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={!can('chat.updateBlockStatus')} onClick={() => setDlg({ kind: 'unblock' })}>
                  <Ban className="mr-2 h-4 w-4" /> Unblock
                </DropdownMenuItem>
                <DropdownMenuItem className="text-red-600" disabled={!can('chat.updateBlockStatus')} onClick={() => setDlg({ kind: 'block' })}>
                  <Ban className="mr-2 h-4 w-4" /> Block
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <h3 className="text-sm font-semibold text-gray-900">{name}</h3>

      {q.isLoading && <Loading what="messages" />}
      {q.isError && <ErrorNote error={q.error} what="messages" />}
      {q.data && records.length === 0 && <Empty title="No messages stored for this chat" hint="Messages appear here once the line has sent or received something in it." />}
      {records.length < total && (
        <Button variant="outline" size="sm" onClick={() => setCount(count + PAGE)} disabled={q.isFetching}>
          {q.isFetching ? 'Loading older messages...' : `Load older messages (${total - records.length} more)`}
        </Button>
      )}
      <div className="space-y-2 rounded-lg bg-gray-50 p-3">{records.map(row)}</div>

      <div className="flex gap-2">
        <Input placeholder="Reply as this line" value={reply} onChange={(e) => setReply(e.target.value)} />
        <ActionButton
          action="message.sendText"
          disabled={!reply.trim() || call.isPending}
          onClick={() => run('message.sendText', { number, text: reply }, 'Sent', () => setReply(''))}
        >
          <Send /> Send
        </ActionButton>
      </div>

      <Dialog open={dlg?.kind === 'react' || dlg?.kind === 'edit'} onOpenChange={(o) => !o && !call.isPending && setDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{dlg?.kind === 'edit' ? 'Edit message' : 'React to message'}</DialogTitle>
            <DialogDescription>
              {dlg?.kind === 'edit' ? 'The edited text replaces the original for everyone in the chat.' : 'Type or paste one reaction character. Leave it empty to remove your reaction.'}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="msg-text">{dlg?.kind === 'edit' ? 'Text' : 'Reaction'}</Label>
            <Input id="msg-text" value={text} onChange={(e) => setText(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)} disabled={call.isPending}>Cancel</Button>
            <Button
              disabled={call.isPending || (dlg?.kind === 'edit' && !text.trim())}
              onClick={() => {
                if (!dlg || !('msg' in dlg) || !dlg.msg) return;
                if (dlg.kind === 'edit') run('chat.updateMessage', { number, key: keyOf(dlg.msg), text }, 'Message edited');
                else run('message.sendReaction', { key: keyOf(dlg.msg), reaction: text }, 'Reaction sent');
              }}
            >
              {call.isPending ? 'Working...' : dlg?.kind === 'edit' ? 'Save edit' : 'Send reaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={dlg?.kind === 'delete' || dlg?.kind === 'block' || dlg?.kind === 'unblock' || dlg?.kind === 'archive'}
        title={
          dlg?.kind === 'delete' ? 'Delete for everyone?' : dlg?.kind === 'block' ? 'Block this contact?' : dlg?.kind === 'unblock' ? 'Unblock this contact?' : 'Archive this chat?'
        }
        body={
          dlg?.kind === 'delete'
            ? 'The message is removed from the chat for every participant. This cannot be undone.'
            : dlg?.kind === 'block'
              ? `${name} will no longer be able to message or call this line.`
              : dlg?.kind === 'unblock'
                ? `${name} will be able to message this line again.`
                : 'The chat moves to the archive on the linked phone.'
        }
        action={dlg?.kind === 'delete' ? 'Delete' : dlg?.kind === 'block' ? 'Block' : dlg?.kind === 'unblock' ? 'Unblock' : 'Archive'}
        pending={call.isPending}
        onConfirm={confirm}
        onClose={() => setDlg(null)}
      />

      <ProfileDialog instance={instance} number={number} open={dlg?.kind === 'profile'} onClose={() => setDlg(null)} />
    </div>
  );
};

const ProfileDialog: React.FC<{ instance: string; number: string; open: boolean; onClose: () => void }> = ({ instance, number, open, onClose }) => {
  const profile = useLineRead<Obj>(instance, 'chat.fetchProfile', { body: { number }, enabled: open && !isGroupJid(number) });
  const pic = useLineRead<Obj>(instance, 'chat.fetchProfilePictureUrl', { body: { number }, enabled: open && !isGroupJid(number) });
  const p = profile.data;
  const url = pic.data?.profilePictureUrl || p?.picture;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Profile</DialogTitle>
          <DialogDescription>{phoneLabel(number)}</DialogDescription>
        </DialogHeader>
        {isGroupJid(number) && <p className="text-sm text-gray-600">This is a group. Open it from the Groups tab to see its details.</p>}
        {(profile.isLoading || pic.isLoading) && !isGroupJid(number) && <Loading what="the profile" />}
        {profile.isError && <ErrorNote error={profile.error} what="the profile" />}
        {p && (
          <div className="space-y-2 text-sm">
            {url && <img src={url} alt="" className="h-24 w-24 rounded-full object-cover" />}
            <div><span className="text-gray-500">Name: </span>{p.name || p.wuid || 'Not shared'}</div>
            <div><span className="text-gray-500">About: </span>{p.status?.status || p.status || 'Not shared'}</div>
            <div><span className="text-gray-500">Business: </span>{p.isBusiness ? 'Yes' : 'No'}</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ChatsTab: React.FC<{ instance: string; connected: boolean }> = ({ instance, connected }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<{ jid: string; name: string } | null>(null);
  const q = useLineRead<unknown>(instance, 'chat.findChats', { body: {}, enabled: connected });
  const chats = asArray<Obj>(q.data);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = chats.filter((c) => !s || `${c.name ?? ''} ${c.pushName ?? ''} ${c.remoteJid ?? ''}`.toLowerCase().includes(s));
    return list.sort((a, b) => String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? '')));
  }, [chats, search]);

  if (!connected) return <Empty title="No phone is linked" hint="Link a phone from the Overview tab to see its chats." />;
  if (open) return <Thread instance={instance} jid={open.jid} name={open.name} onBack={() => setOpen(null)} />;

  return (
    <div className="space-y-3">
      <Input placeholder="Search chats by name or number" value={search} onChange={(e) => setSearch(e.target.value)} />
      {q.isLoading && <Loading what="chats" />}
      {q.isError && <ErrorNote error={q.error} what="chats" />}
      {q.isSuccess && shown.length === 0 && (
        <Empty
          title={search ? 'No chat matches that search' : 'No chats yet'}
          hint={search ? 'Clear the search to see every chat.' : 'Chats appear after the line sends or receives a message. Use the Send tab to start one.'}
        />
      )}
      <ul className="divide-y rounded-lg border border-gray-200 bg-white">
        {shown.map((c) => {
          const jid = c.remoteJid || c.id;
          const name = c.name || c.pushName || phoneLabel(jid);
          const last = messageText(c.lastMessage);
          return (
            <li key={jid}>
              <button type="button" className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50" onClick={() => setOpen({ jid, name })}>
                {c.profilePicUrl ? (
                  <img src={c.profilePicUrl} alt="" className="h-9 w-9 flex-shrink-0 rounded-full object-cover" />
                ) : (
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100"><User className="h-4 w-4 text-gray-400" /></div>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{name}</span>
                  <span className="block truncate text-xs text-gray-500">{last || phoneLabel(jid)}</span>
                </span>
                {c.unreadMessages > 0 && <span className="rounded-full bg-green-600 px-2 py-0.5 text-xs text-white">{c.unreadMessages}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ChatsTab;
